import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Edit, Building2, Globe, CreditCard, ChevronDown, ChevronUp, History, X, Check, Upload, Download, FileText, CheckCircle2, XCircle } from 'lucide-react';
import { investorAccountAPI } from '../../services/api';

const RECORD_TYPES = [
  { value: 'indian_bank', label: 'Indian Bank Account', icon: Building2 },
  { value: 'international_bank', label: 'International Bank Account', icon: Globe },
  { value: 'demat', label: 'Demat Account', icon: CreditCard },
];

const DEFAULT_ACCOUNT_TYPES = ['Savings', 'Current', 'NRE', 'NRO'];
const DEPOSITORIES = ['CDSL', 'NSDL'];

/**
 * PanMatchBadge — inline validation indicator next to Client PAN in demat
 * records. Green tick when it matches the account-level PAN, red cross
 * when it doesn't, hidden when either side is empty (nothing to compare).
 *
 * Comparison is case-insensitive + whitespace-tolerant. Match uses a
 * `text-green-600` tick; mismatch a `text-red-600` cross with a tooltip
 * showing the expected value so ops can spot the issue at a glance.
 */
const PanMatchBadge = ({ clientPan, accountPan, testid }) => {
  const cp = (clientPan || '').toUpperCase().trim();
  const ap = (accountPan || '').toUpperCase().trim();
  if (!cp || !ap) return null;
  const match = cp === ap;
  return match ? (
    <span className="inline-flex items-center text-green-600" title="Client PAN matches Account PAN" data-testid={testid || 'pan-match-ok'}>
      <CheckCircle2 size={14} className="ml-1" />
    </span>
  ) : (
    <span className="inline-flex items-center text-red-600" title={`Does not match Account PAN (${ap})`} data-testid={testid || 'pan-match-fail'}>
      <XCircle size={14} className="ml-1" />
    </span>
  );
};

export default function BankDematTab({ accountId, canEdit, accountPan, canDelete = false }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formType, setFormType] = useState('indian_bank');
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [accountTypes, setAccountTypes] = useState(DEFAULT_ACCOUNT_TYPES);
  const [showCustomType, setShowCustomType] = useState(false);
  const [customTypeInput, setCustomTypeInput] = useState('');
  const fileInputRef = useRef({});

  useEffect(() => { loadRecords(); }, [accountId]);

  const loadRecords = async () => {
    try {
      const res = await investorAccountAPI.getBankDemat(accountId);
      setRecords(res.data || []);
    } catch {} finally { setLoading(false); }
  };

  const initForm = (type) => {
    setFormType(type);
    if (type === 'indian_bank') setForm({ account_number: '', bank_name: '', ifsc_code: '', account_type: 'Savings' });
    else if (type === 'international_bank') setForm({ account_number: '', bank_name: '', swift_code: '', iban: '', correspondence_bank_name: '', correspondence_account_number: '', correspondence_swift_code: '' });
    else setForm({ depository: 'CDSL', dp_id: '', dp_name: '', client_id: '', client_name_as_per_cml: '', client_pan: '' });
  };

  const startCreate = (type) => { initForm(type); setEditingRecord(null); setFormError(null); setShowForm(true); };

  const startEdit = (rec) => {
    setEditingRecord(rec);
    setFormType(rec.record_type);
    setForm(rec[rec.record_type] || {});
    setFormError(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    // Client-side PAN validation for Demat
    if (formType === 'demat' && form.client_pan && accountPan) {
      const cp = (form.client_pan || '').toUpperCase().trim();
      const ap = (accountPan || '').toUpperCase().trim();
      if (cp && ap && cp !== ap) {
        if (!window.confirm(`Client PAN (${cp}) does not match Account PAN (${ap}). Save anyway?`)) return;
      }
    }
    // Reason for change is mandatory on edits (label already shows *)
    if (editingRecord && !(form.change_remarks || '').trim()) {
      setFormError('Reason for change is required — it is saved to the change history.');
      return;
    }
    setFormError(null);
    const payload = { record_type: formType, [formType]: form, remarks: form.remarks || '' };
    if (editingRecord) payload.change_remarks = form.change_remarks.trim();
    try {
      if (editingRecord) {
        await investorAccountAPI.updateBankDemat(accountId, editingRecord.id, payload);
      } else {
        await investorAccountAPI.createBankDemat(accountId, payload);
      }
      setShowForm(false); setEditingRecord(null);
      loadRecords();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleUploadDoc = async (recId, file) => {
    if (!file) return;
    setUploadingId(recId);
    try {
      await investorAccountAPI.uploadBankDematDoc(accountId, recId, file);
      await loadRecords();
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingId(null); }
  };

  const handleDownloadDoc = async (recId) => {
    try {
      const res = await investorAccountAPI.downloadBankDematDoc(accountId, recId);
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch (err) { alert(err.response?.data?.detail || 'Download failed'); }
  };

  const handleDelete = async (recId) => {
    if (!window.confirm('Delete this record?')) return;
    await investorAccountAPI.deleteBankDemat(accountId, recId);
    loadRecords();
  };

  const renderField = (label, value) => (
    <div className="text-sm"><span className="text-gray-500 text-xs">{label}</span><p className="font-medium text-gray-900">{value || '--'}</p></div>
  );

  if (loading) return <div className="py-8 text-center text-gray-500">Loading...</div>;

  return (
    <div data-testid="tab-content-bank-demat">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Bank & Demat Accounts</h3>
        {canEdit && (
          <div className="relative group">
            <button className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="add-bank-demat-btn">
              <Plus size={14} className="mr-1" /> Add Record
            </button>
            <div className="absolute right-0 mt-1 w-52 bg-white border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
              {RECORD_TYPES.map(t => (
                <button key={t.value} onClick={() => startCreate(t.value)} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2">
                  <t.icon size={14} /> {t.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-gray-50 border rounded-lg p-5 mb-4" data-testid="bank-demat-form">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">{editingRecord ? 'Edit' : 'Add'} {RECORD_TYPES.find(t => t.value === formType)?.label}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {formType === 'indian_bank' && (<>
              <div><label className="text-xs font-medium text-gray-600">Account Number *</label><input type="text" value={form.account_number || ''} onChange={e => setForm(p => ({ ...p, account_number: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
              <div><label className="text-xs font-medium text-gray-600">Bank Name *</label><input type="text" value={form.bank_name || ''} onChange={e => setForm(p => ({ ...p, bank_name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
              <div><label className="text-xs font-medium text-gray-600">IFSC Code</label><input type="text" value={form.ifsc_code || ''} onChange={e => setForm(p => ({ ...p, ifsc_code: e.target.value.toUpperCase() }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5 font-mono" /></div>
              <div><label className="text-xs font-medium text-gray-600">Account Type</label>
                <div className="flex gap-1">
                  <select value={form.account_type || 'Savings'} onChange={e => { if (e.target.value === '__custom__') { setShowCustomType(true); } else { setForm(p => ({ ...p, account_type: e.target.value })); } }} className="flex-1 border rounded-lg px-3 py-2 text-sm mt-0.5">
                    {accountTypes.map(t => <option key={t}>{t}</option>)}
                    <option value="__custom__">+ Add custom...</option>
                  </select>
                </div>
                {showCustomType && (
                  <div className="flex gap-1 mt-1">
                    <input type="text" value={customTypeInput} onChange={e => setCustomTypeInput(e.target.value)} placeholder="Enter custom type" className="flex-1 border rounded-lg px-2 py-1 text-xs" />
                    <button type="button" onClick={() => { if (customTypeInput.trim()) { setAccountTypes(p => [...p, customTypeInput.trim()]); setForm(p => ({ ...p, account_type: customTypeInput.trim() })); setCustomTypeInput(''); setShowCustomType(false); } }} className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Add</button>
                    <button type="button" onClick={() => { setShowCustomType(false); setCustomTypeInput(''); }} className="px-2 py-1 border rounded text-xs">Cancel</button>
                  </div>
                )}
              </div>
            </>)}
            {formType === 'international_bank' && (<>
              <div><label className="text-xs font-medium text-gray-600">Account Number *</label><input type="text" value={form.account_number || ''} onChange={e => setForm(p => ({ ...p, account_number: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
              <div><label className="text-xs font-medium text-gray-600">Bank Name *</label><input type="text" value={form.bank_name || ''} onChange={e => setForm(p => ({ ...p, bank_name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
              <div><label className="text-xs font-medium text-gray-600">SWIFT Code</label><input type="text" value={form.swift_code || ''} onChange={e => setForm(p => ({ ...p, swift_code: e.target.value.toUpperCase() }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5 font-mono" /></div>
              <div><label className="text-xs font-medium text-gray-600">IBAN</label><input type="text" value={form.iban || ''} onChange={e => setForm(p => ({ ...p, iban: e.target.value.toUpperCase() }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5 font-mono" /></div>
              <div><label className="text-xs font-medium text-gray-600">Correspondence Bank</label><input type="text" value={form.correspondence_bank_name || ''} onChange={e => setForm(p => ({ ...p, correspondence_bank_name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
              <div><label className="text-xs font-medium text-gray-600">Correspondence Account No.</label><input type="text" value={form.correspondence_account_number || ''} onChange={e => setForm(p => ({ ...p, correspondence_account_number: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
              <div><label className="text-xs font-medium text-gray-600">Correspondence SWIFT</label><input type="text" value={form.correspondence_swift_code || ''} onChange={e => setForm(p => ({ ...p, correspondence_swift_code: e.target.value.toUpperCase() }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5 font-mono" /></div>
            </>)}
            {formType === 'demat' && (<>
              <div><label className="text-xs font-medium text-gray-600">Depository</label><select value={form.depository || 'CDSL'} onChange={e => setForm(p => ({ ...p, depository: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5">{DEPOSITORIES.map(d => <option key={d}>{d}</option>)}</select></div>
              <div><label className="text-xs font-medium text-gray-600">DP ID</label><input type="text" value={form.dp_id || ''} onChange={e => setForm(p => ({ ...p, dp_id: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
              <div><label className="text-xs font-medium text-gray-600">DP Name</label><input type="text" value={form.dp_name || ''} onChange={e => setForm(p => ({ ...p, dp_name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
              <div><label className="text-xs font-medium text-gray-600">Client ID</label><input type="text" value={form.client_id || ''} onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
              <div><label className="text-xs font-medium text-gray-600">Client Name (as per CML)</label><input type="text" value={form.client_name_as_per_cml || ''} onChange={e => setForm(p => ({ ...p, client_name_as_per_cml: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
              <div><label className="text-xs font-medium text-gray-600">Client PAN</label><input type="text" value={form.client_pan || ''} onChange={e => setForm(p => ({ ...p, client_pan: e.target.value.toUpperCase() }))} maxLength={10} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5 font-mono" /></div>
            </>)}
          </div>
          <div className="mt-3"><label className="text-xs font-medium text-gray-600">Remarks</label><textarea value={form.remarks || ''} onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm mt-0.5 resize-none" /></div>
          {editingRecord && (
            <div className="mt-3"><label className="text-xs font-medium text-amber-700">Reason for change *</label><textarea value={form.change_remarks || ''} onChange={e => { setForm(p => ({ ...p, change_remarks: e.target.value })); setFormError(null); }} rows={2} placeholder="Briefly describe why these details are being updated (will be saved in change history)" className={`w-full border rounded-lg px-3 py-2 text-sm mt-0.5 resize-none bg-amber-50 ${formError ? 'border-red-500' : 'border-amber-200'}`} data-testid="change-remarks-input" /></div>
          )}
          {formError && <p className="text-red-600 text-xs mt-2" data-testid="bank-demat-form-error">{formError}</p>}
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setShowForm(false); setEditingRecord(null); }} className="px-3 py-2 border rounded-lg text-sm">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="save-bank-demat-btn"><Check size={14} className="inline mr-1" /> Save</button>
          </div>
        </div>
      )}

      {/* Records list */}
      {records.length === 0 ? (
        <div className="text-center py-10 bg-white border rounded-lg text-gray-400">
          <Building2 size={32} className="mx-auto mb-2" /><p className="text-sm">No bank or demat records added yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {records.map(rec => {
            const rt = RECORD_TYPES.find(t => t.value === rec.record_type);
            const Icon = rt?.icon || Building2;
            const data = rec[rec.record_type] || {};
            return (
              <div key={rec.id} className={`border rounded-lg p-4 ${!rec.is_active ? 'opacity-50 bg-gray-50' : 'bg-white'}`} data-testid={`bank-demat-${rec.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon size={16} className="text-blue-600" />
                    <span className="text-sm font-semibold text-gray-800">{rt?.label}</span>
                    {!rec.is_active && <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">INACTIVE</span>}
                  </div>
                  {(canEdit || canDelete) && (
                    <div className="flex gap-1">
                      {canEdit && (
                        <button onClick={() => startEdit(rec)} className="p-1 text-blue-500 hover:text-blue-700"><Edit size={14} /></button>
                      )}
                      {rec.change_history?.length > 0 && (
                        <button onClick={() => setExpandedHistory(expandedHistory === rec.id ? null : rec.id)} className="p-1 text-gray-400 hover:text-gray-600" title="View history"><History size={14} /></button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDelete(rec.id)} className="p-1 text-gray-400 hover:text-red-500" title="Delete (Admin only)"><Trash2 size={14} /></button>
                      )}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {rec.record_type === 'indian_bank' && (<>
                    {renderField('Account Number', data.account_number)}
                    {renderField('Bank Name', data.bank_name)}
                    {renderField('IFSC Code', data.ifsc_code)}
                    {renderField('Account Type', data.account_type)}
                  </>)}
                  {rec.record_type === 'international_bank' && (<>
                    {renderField('Account Number', data.account_number)}
                    {renderField('Bank Name', data.bank_name)}
                    {renderField('SWIFT', data.swift_code)}
                    {renderField('IBAN', data.iban)}
                    {data.correspondence_bank_name && renderField('Corr. Bank', data.correspondence_bank_name)}
                    {data.correspondence_swift_code && renderField('Corr. SWIFT', data.correspondence_swift_code)}
                  </>)}
                  {rec.record_type === 'demat' && (<>
                    {renderField('Depository', data.depository)}
                    {renderField('DP ID', data.dp_id)}
                    {renderField('DP Name', data.dp_name)}
                    {renderField('Client ID', data.client_id)}
                    {renderField('Name (CML)', data.client_name_as_per_cml)}
                    <div className="text-sm">
                      <span className="text-gray-500 text-xs flex items-center">
                        Client PAN
                        <PanMatchBadge clientPan={data.client_pan} accountPan={accountPan} testid={`pan-match-${rec.id}`} />
                      </span>
                      <p className="font-medium text-gray-900 font-mono">{data.client_pan || '--'}</p>
                    </div>
                  </>)}
                </div>
                {rec.remarks && <p className="text-xs text-gray-500 mt-2 italic">Remarks: {rec.remarks}</p>}
                {/* Supporting Doc */}
                <div className="mt-2 flex items-center gap-2 text-xs">
                  {rec.supporting_doc_url ? (
                    <button onClick={() => handleDownloadDoc(rec.id)} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700" data-testid={`download-doc-${rec.id}`}>
                      <FileText size={12} /> {rec.supporting_doc_name || 'Document'} <Download size={12} />
                    </button>
                  ) : <span className="text-gray-400">No document attached</span>}
                  {canEdit && (
                    <>
                      <input ref={el => { fileInputRef.current[rec.id] = el; }} type="file" hidden onChange={e => { handleUploadDoc(rec.id, e.target.files?.[0]); e.target.value = ''; }} />
                      <button onClick={() => fileInputRef.current[rec.id]?.click()} disabled={uploadingId === rec.id} className="inline-flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50" data-testid={`upload-doc-${rec.id}`}>
                        <Upload size={12} /> {uploadingId === rec.id ? 'Uploading...' : (rec.supporting_doc_url ? 'Replace' : 'Upload')}
                      </button>
                    </>
                  )}
                </div>
                {/* Change History */}
                {expandedHistory === rec.id && rec.change_history?.length > 0 && (
                  <div className="mt-3 border-t pt-2" data-testid={`history-${rec.id}`}>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Change History ({rec.change_history.length})</p>
                    <div className="space-y-2">
                      {[...rec.change_history].reverse().map((h, i) => {
                        const prev = h.previous || {};
                        // What it was changed TO: prefer h.new, otherwise fall back to next-newer entry's previous, otherwise current record
                        const reversed = [...rec.change_history].reverse();
                        const next = h.new || (i > 0 ? (reversed[i - 1]?.previous || {}) : { indian_bank: rec.indian_bank, international_bank: rec.international_bank, demat: rec.demat, remarks: rec.remarks });
                        const subKey = rec.record_type;
                        const oldData = prev[subKey] || {};
                        const newData = next[subKey] || {};
                        const oldRemarks = prev.remarks || '';
                        const newRemarks = next.remarks || '';

                        const allKeys = Array.from(new Set([...Object.keys(oldData), ...Object.keys(newData)]));
                        const diffRows = allKeys
                          .filter(k => String(oldData[k] || '') !== String(newData[k] || ''))
                          .map(k => ({ key: k, old: oldData[k], new: newData[k] }));
                        if (oldRemarks !== newRemarks) diffRows.push({ key: 'remarks', old: oldRemarks, new: newRemarks });

                        return (
                          <div key={i} className="bg-gray-50 border rounded p-2">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-700">
                                <span className="font-medium">{h.changed_by}</span>
                                <span className="text-gray-400 ml-1">on {new Date(h.changed_at).toLocaleString()}</span>
                              </p>
                            </div>
                            {h.change_remarks && (
                              <p className="text-xs text-amber-700 italic mb-1">Reason: {h.change_remarks}</p>
                            )}
                            {diffRows.length === 0 ? (
                              <p className="text-[11px] text-gray-400 italic">No field-level changes detected</p>
                            ) : (
                              <table className="w-full text-[11px]">
                                <thead>
                                  <tr className="text-gray-500">
                                    <th className="text-left font-medium pr-2 w-28">Field</th>
                                    <th className="text-left font-medium pr-2">Old Value</th>
                                    <th className="text-left font-medium">New Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {diffRows.map(d => (
                                    <tr key={d.key} className="align-top">
                                      <td className="pr-2 text-gray-600">{d.key.replace(/_/g, ' ')}</td>
                                      <td className="pr-2 line-through text-red-600 break-all">{String(d.old || '—')}</td>
                                      <td className="text-green-700 break-all">{String(d.new || '—')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
